import React, { useMemo } from 'react'
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer'
import ItineraryReportHeaderPdf from './ItineraryReportHeaderPdf.component'
import ItineraryReportFooterPdf from './ItineraryReportFooterPdf.component'
import { ItineraryReportPdfProps, CompanyFeature } from './ItineraryReportPdf.types'
import { sizeColors } from '~/design'

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 65,
    paddingHorizontal: 40,
  },

  titleContainer: {
    marginBottom: 15,
  },

  title: {
    fontSize: 18,
    color: '#0F438A',
    fontFamily: 'Lato-700',
  },

  sectionTitle: {
    fontSize: 14,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginTop: 5,
    marginBottom: 5,
  },

  table: {
    display: 'flex',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#B6BFC8',
    borderRadius: 4,
    marginBottom: 15,
  },

  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    borderBottomWidth: 1,
    borderBottomColor: '#B6BFC8',
    paddingVertical: 8,
  },

  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#EFEFEF',
    paddingVertical: 8,
    odd: {
      backgroundColor: '#FAFAFA',
    },
  },

  tableColHeader: {
    width: '70%',
    paddingLeft: 10,
    fontSize: 12,
    fontFamily: 'Lato-700',
    color: '#0F438A',
  },

  tableColHeaderQty: {
    width: '30%',
    paddingLeft: 10,
    fontSize: 12,
    fontFamily: 'Lato-700',
    color: '#0F438A',
  },

  tableCol: {
    width: '70%',
    paddingLeft: 10,
    fontSize: 12,
    color: '#363636',
    lineHeight: 1,
  },

  tableColAlign: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 10,
  },

  sizeColor: {
    width: 12,
    height: 12,
    borderRadius: 4,
  },

  tableColQty: {
    width: '30%',
    paddingLeft: 10,
    fontSize: 10,
    color: '#363636',
    lineHeight: 1,
  },
})

const LINE_NUMBER_LIMIT_PER_PAGE = 13

export default function ItineraryReportStatsPdf({
  itinerary,
  generatedAt,
}: ItineraryReportPdfProps) {
  const stats = useMemo(() => {
    const companies = itinerary.features.filter(
      (f: CompanyFeature) => f.properties?.kind === 'interestPoint',
    )

    const sizeBreakdown: number[] = companies.reduce(
      (acc: Record<string, number>, f: CompanyFeature) => {
        const size = f.properties?.size || 'Não informado'
        acc[size] = (acc[size] || 0) + 1
        return acc
      },
      {},
    )

    const segmentBreakdown: number[] = companies.reduce(
      (acc: Record<string, number>, f: CompanyFeature) => {
        const segment = f.properties?.segmentDescription || 'Não informado'
        acc[segment] = (acc[segment] || 0) + 1
        return acc
      },
      {},
    )

    const cnaeBreakdown: number[] = companies.reduce(
      (acc: Record<string, number>, f: CompanyFeature) => {
        const cnae = f.properties?.mainCnaeDescription || 'Não informado'
        acc[cnae] = (acc[cnae] || 0) + 1
        return acc
      },
      {},
    )

    return {
      sizeBreakdown: Object.entries(sizeBreakdown).sort((a, b) => b[1] - a[1]),
      segmentBreakdown: Object.entries(segmentBreakdown).sort((a, b) => b[1] - a[1]),
      cnaeBreakdown: Object.entries(cnaeBreakdown).sort((a, b) => b[1] - a[1]),
    }
  }, [itinerary])

  const listSegments = useMemo(() => {
    const firstPageLimit = LINE_NUMBER_LIMIT_PER_PAGE - stats.sizeBreakdown.length
    const firstPageSegments = stats.segmentBreakdown.slice(
      0,
      firstPageLimit > 0 ? firstPageLimit : 0,
    )
    const secondPageSegments =
      firstPageLimit > 0 ? stats.segmentBreakdown.slice(firstPageLimit) : []

    const pages = [firstPageSegments, secondPageSegments].filter(page => page.length > 0)

    return pages.map((segments, pageIndex) => (
      <View key={pageIndex} style={styles.table} break={pageIndex > 0}>
        {pageIndex === 0 && (
          <View style={styles.tableHeader}>
            <Text style={styles.tableColHeader}>Segmento</Text>
            <Text style={styles.tableColHeaderQty}>Quantidade</Text>
          </View>
        )}
        {segments.map(([segment, count], index) => (
          <View
            key={index}
            style={{ ...styles.tableRow, backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
          >
            <Text style={styles.tableCol}>{segment}</Text>
            <Text style={styles.tableColQty}>{count}</Text>
          </View>
        ))}
      </View>
    ))
  }, [stats.segmentBreakdown])

  const listCnaes = useMemo(() => {
    const firstPageLimit =
      LINE_NUMBER_LIMIT_PER_PAGE - stats.sizeBreakdown.length - stats.segmentBreakdown.length
    const firstPageCnaes = stats.cnaeBreakdown.slice(0, firstPageLimit > 0 ? firstPageLimit : 0)
    const secondPageCnaes = firstPageLimit > 0 ? stats.cnaeBreakdown.slice(firstPageLimit) : []

    const pages = [firstPageCnaes, secondPageCnaes].filter(page => page.length > 0)

    return pages.map((cnaes, pageIndex) => (
      <View key={pageIndex} style={styles.table} break={pageIndex > 0}>
        {pageIndex === 0 && (
          <View style={styles.tableHeader}>
            <Text style={styles.tableColHeader}>CNAE</Text>
            <Text style={styles.tableColHeaderQty}>Quantidade</Text>
          </View>
        )}
        {cnaes.map(([cnae, count], index) => (
          <View
            key={index}
            style={{ ...styles.tableRow, backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
          >
            <Text style={styles.tableCol}>{cnae}</Text>
            <Text style={styles.tableColQty}>{count}</Text>
          </View>
        ))}
      </View>
    ))
  }, [stats.cnaeBreakdown])

  return (
    <Page size='A4' style={styles.page}>
      <ItineraryReportHeaderPdf />

      <View style={styles.titleContainer}>
        <Text style={styles.title}>Estatísticas do Itinerário</Text>
      </View>

      {/* Portes */}
      <Text style={styles.sectionTitle}>Portes Presentes na Rota</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableColHeader}>Porte</Text>
          <Text style={styles.tableColHeaderQty}>Quantidade</Text>
        </View>
        {stats.sizeBreakdown.map(([size, count], index) => (
          <View
            key={index}
            style={{ ...styles.tableRow, backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
          >
            <View style={styles.tableColAlign}>
              <View
                style={{
                  ...styles.sizeColor,
                  backgroundColor:
                    sizeColors[size as keyof typeof sizeColors] || sizeColors['Others'],
                }}
              />
              <Text style={styles.tableCol}>{size}</Text>
            </View>
            <Text style={styles.tableColQty}>{count}</Text>
          </View>
        ))}
      </View>

      {/* Segmentos */}
      <Text style={styles.sectionTitle}>Segmentos Presentes na Rota</Text>
      {listSegments}

      {/* CNAE */}
      <Text
        break={stats.sizeBreakdown.length + stats.segmentBreakdown.length > 13}
        style={styles.sectionTitle}
      >
        Principais CNAEs na Rota
      </Text>
      {listCnaes}

      <ItineraryReportFooterPdf generatedAt={generatedAt} />
    </Page>
  )
}
